/**
 * TUPU Personas — Centralized Permission System
 * ================================================
 * Levels: NONE, READ, MANAGE (MANAGE > READ > NONE)
 */

export const NONE = 'NONE';
export const READ = 'READ';
export const MANAGE = 'MANAGE';

const HIERARCHY = { NONE: 0, READ: 1, MANAGE: 2 };

export const ALL_MODULES = [
  'dashboard', 'turnos', 'asistencia', 'costo_laboral', 'dotacion',
  'planilla_mensual', 'horas_extras', 'historial_anticipos',
  'remuneraciones', 'trabajadores', 'cuentas_bancarias', 'reportes',
  'sucursales', 'usuarios', 'mes_operativo', 'configuracion',
];

export const MODULE_LABELS = {
  dashboard: 'Dashboard',
  turnos: 'Turnos',
  asistencia: 'Asistencia',
  costo_laboral: 'Costo Laboral',
  dotacion: 'Dotacion',
  planilla_mensual: 'Planilla Mensual',
  horas_extras: 'Horas Extras',
  historial_anticipos: 'Historial Anticipos',
  remuneraciones: 'Remuneraciones',
  trabajadores: 'Trabajadores',
  cuentas_bancarias: 'Cuentas Bancarias',
  reportes: 'Reportes',
  sucursales: 'Sucursales',
  usuarios: 'Usuarios',
  mes_operativo: 'Mes Operativo',
  configuracion: 'Configuracion',
};

// Maps sidebar paths to module keys
export const PATH_TO_MODULE = {
  '/': 'dashboard',
  '/turnos': 'turnos',
  '/asistencia-admin': 'asistencia',
  '/costo-laboral': 'costo_laboral',
  '/dotacion': 'dotacion',
  '/planilla': 'planilla_mensual',
  '/horas-extras': 'horas_extras',
  '/anticipos': 'historial_anticipos',
  '/remuneraciones': 'remuneraciones',
  '/trabajadores': 'trabajadores',
  '/cuentas-bancarias': 'cuentas_bancarias',
  '/reportes': 'reportes',
  '/sucursales': 'sucursales',
  '/usuarios': 'usuarios',
  '/mes-operativo': 'mes_operativo',
  '/configuracion': 'configuracion',
};

// Role templates for fallback when user has no module_permissions
const ROLE_TEMPLATES = {
  jefe_local: {
    dashboard: READ, turnos: MANAGE, asistencia: MANAGE,
    costo_laboral: READ, dotacion: READ,
    planilla_mensual: READ, horas_extras: READ,
    historial_anticipos: READ, remuneraciones: READ,
    trabajadores: MANAGE, cuentas_bancarias: MANAGE,
    reportes: READ, sucursales: NONE,
    usuarios: NONE, mes_operativo: NONE, configuracion: NONE,
  },
  encargado: {
    dashboard: READ, turnos: NONE, asistencia: READ,
    costo_laboral: NONE, dotacion: NONE,
    planilla_mensual: READ, horas_extras: READ,
    historial_anticipos: READ, remuneraciones: READ,
    trabajadores: READ, cuentas_bancarias: NONE,
    reportes: READ, sucursales: NONE,
    usuarios: NONE, mes_operativo: NONE, configuracion: NONE,
  },
  encargado_turno: {
    dashboard: READ, turnos: MANAGE, asistencia: READ,
    costo_laboral: NONE, dotacion: NONE,
    planilla_mensual: READ, horas_extras: READ,
    historial_anticipos: NONE, remuneraciones: NONE,
    trabajadores: READ, cuentas_bancarias: NONE,
    reportes: READ, sucursales: NONE,
    usuarios: NONE, mes_operativo: NONE, configuracion: NONE,
  },
  encargado_remuneraciones: {
    dashboard: READ, turnos: NONE, asistencia: READ,
    costo_laboral: READ, dotacion: READ,
    planilla_mensual: READ, horas_extras: READ,
    historial_anticipos: MANAGE, remuneraciones: MANAGE,
    trabajadores: READ, cuentas_bancarias: MANAGE,
    reportes: READ, sucursales: NONE,
    usuarios: NONE, mes_operativo: READ, configuracion: NONE,
  },
};

/**
 * Get effective permission for a user on a module.
 * Admin always gets MANAGE.
 * Backend resolves permissions in login/me response, so module_permissions is already effective.
 */
export function getEffectivePermission(user, module) {
  if (!user) return NONE;
  if (user.role === 'admin') return MANAGE;
  const perms = user.module_permissions || {};
  // If backend already resolved (non-empty), use directly
  if (Object.keys(perms).length > 0) {
    return perms[module] || NONE;
  }
  // Fallback for edge cases (stale token, etc)
  const template = ROLE_TEMPLATES[user.role];
  return template ? (template[module] || NONE) : NONE;
}

/**
 * Check if user has at least the required permission level.
 */
export function hasPermission(user, module, required = READ) {
  const effective = getEffectivePermission(user, module);
  return (HIERARCHY[effective] || 0) >= (HIERARCHY[required] || 0);
}

/**
 * Convenience: can user access (read) a module?
 */
export function canRead(user, module) {
  return hasPermission(user, module, READ);
}

/**
 * Convenience: can user manage (write) a module?
 */
export function canManage(user, module) {
  return hasPermission(user, module, MANAGE);
}

/**
 * Count modules with access (READ or MANAGE)
 */
export function countAccessibleModules(modulePerms) {
  if (!modulePerms) return 0;
  return Object.values(modulePerms).filter(v => v === READ || v === MANAGE).length;
}

export function countManageableModules(modulePerms) {
  if (!modulePerms) return 0;
  return Object.values(modulePerms).filter(v => v === MANAGE).length;
}
